import React, { useState } from 'react';
import {
  Alert, Avatar, Box, Button, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, IconButton, Stack, TextField, Typography,
} from '@mui/material';
import { Close, ContentCopy, LockReset, Security } from '@mui/icons-material';
import { useSelector } from 'react-redux';

import { RootState } from '@/store';
import { authService } from '@/services/auth/auth.service';

type Props = {
  open: boolean;
  onClose: () => void;
};

type SetupData = {
  qr_code: string;
  manual_key: string;
};

const errorMessage = (error: unknown) => {
  const candidate = error as { response?: { data?: { detail?: string | { message?: string } } } };
  const detail = candidate.response?.data?.detail;
  return typeof detail === 'string' ? detail : detail?.message || 'Не удалось выполнить операцию';
};

export const ProfileDialog: React.FC<Props> = ({ open, onClose }) => {
  const user = useSelector((state: RootState) => state.auth.user);
  const [setup, setSetup] = useState<SetupData | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [passwords, setPasswords] = useState({ current: '', next: '', confirm: '' });
  const [passwordTotp, setPasswordTotp] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  if (!user) return null;

  const refreshUser = async () => authService.me();

  const beginSetup = async () => {
    setBusy(true);
    setNotice(null);
    try {
      setSetup(await authService.setup2fa());
      setRecoveryCodes([]);
    } catch (error) {
      setNotice({ type: 'error', text: errorMessage(error) });
    } finally {
      setBusy(false);
    }
  };

  const confirmSetup = async () => {
    setBusy(true);
    setNotice(null);
    try {
      const result = await authService.confirm2fa(totpCode);
      setRecoveryCodes(result.recovery_codes);
      setSetup(null);
      setTotpCode('');
      await refreshUser();
      setNotice({ type: 'success', text: '2FA подключена. Сохраните резервные коды.' });
    } catch (error) {
      setNotice({ type: 'error', text: errorMessage(error) });
    } finally {
      setBusy(false);
    }
  };

  const disable2fa = async () => {
    setBusy(true);
    setNotice(null);
    try {
      await authService.disable2fa(totpCode);
      setTotpCode('');
      setRecoveryCodes([]);
      await refreshUser();
      setNotice({ type: 'success', text: '2FA отключена, доверенные устройства отозваны.' });
    } catch (error) {
      setNotice({ type: 'error', text: errorMessage(error) });
    } finally {
      setBusy(false);
    }
  };

  const changePassword = async () => {
    if (passwords.next.length < 8) {
      setNotice({ type: 'error', text: 'Новый пароль должен содержать не менее 8 символов.' });
      return;
    }
    if (passwords.next !== passwords.confirm) {
      setNotice({ type: 'error', text: 'Новые пароли не совпадают.' });
      return;
    }
    setBusy(true);
    setNotice(null);
    try {
      await authService.changePassword(passwords.current, passwords.next, passwordTotp);
      setNotice({ type: 'success', text: 'Пароль изменён. Выполняется выход из аккаунта…' });
      window.setTimeout(() => authService.logout(), 700);
    } catch (error) {
      setNotice({ type: 'error', text: errorMessage(error) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 6 }}>
        <Avatar sx={{ bgcolor: 'primary.main' }}>{user.username.slice(0, 1).toUpperCase()}</Avatar>
        <Box>
          <Typography variant="h6">Профиль</Typography>
          <Typography variant="body2" color="text.secondary">{user.username}</Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 12, top: 12 }}><Close /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          {notice && <Alert severity={notice.type}>{notice.text}</Alert>}

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
            <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary">Имя пользователя</Typography>
              <Typography fontWeight={600}>{user.username}</Typography>
            </Box>
            <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary">Email</Typography>
              <Typography fontWeight={600}>{user.email}</Typography>
            </Box>
          </Box>

          <Divider />
          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
              <Stack direction="row" alignItems="center" spacing={1}><Security color="primary" /><Typography variant="h6">Двухфакторная защита</Typography></Stack>
              <Chip label={user.totp_enabled ? 'Подключена' : 'Не подключена'} color={user.totp_enabled ? 'success' : 'default'} size="small" />
            </Stack>
            {!user.totp_enabled && !setup && <Button variant="contained" disabled={busy} onClick={beginSetup}>Подключить 2FA</Button>}
            {setup && (
              <Stack spacing={1.5} alignItems="flex-start">
                <Typography variant="body2">Отсканируйте QR-код в Google Authenticator, Microsoft Authenticator или другом TOTP-приложении.</Typography>
                <Box component="img" src={setup.qr_code} alt="QR-код 2FA" sx={{ width: 180, height: 180, bgcolor: '#fff', p: 1, borderRadius: 2 }} />
                <Button size="small" startIcon={<ContentCopy />} onClick={() => navigator.clipboard.writeText(setup.manual_key)}>Скопировать ключ вручную</Button>
                <TextField fullWidth label="Код из приложения" value={totpCode} onChange={(e) => setTotpCode(e.target.value)} inputProps={{ inputMode: 'numeric' }} />
                <Button variant="contained" disabled={busy || !totpCode} onClick={confirmSetup}>Подтвердить подключение</Button>
              </Stack>
            )}
            {user.totp_enabled && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField size="small" label="Код 2FA или резервный код" value={totpCode} onChange={(e) => setTotpCode(e.target.value)} sx={{ flex: 1 }} />
                <Button color="error" variant="outlined" disabled={busy || !totpCode} onClick={disable2fa}>Отключить 2FA</Button>
              </Stack>
            )}
            {recoveryCodes.length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography fontWeight={700} mb={1}>Резервные коды показываются только один раз</Typography>
                <Box component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{recoveryCodes.join('\n')}</Box>
                <Button size="small" startIcon={<ContentCopy />} onClick={() => navigator.clipboard.writeText(recoveryCodes.join('\n'))}>Скопировать</Button>
              </Alert>
            )}
          </Box>

          <Divider />
          <Box>
            <Stack direction="row" alignItems="center" spacing={1} mb={1.5}><LockReset color="primary" /><Typography variant="h6">Смена пароля</Typography></Stack>
            <Stack spacing={1.5}>
              <TextField type="password" name="admin-current-password-manual" autoComplete="new-password" label="Текущий пароль" value={passwords.current} onChange={(e) => setPasswords({ ...passwords, current: e.target.value })} />
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                <TextField type="password" name="admin-new-password" autoComplete="new-password" label="Новый пароль" value={passwords.next} onChange={(e) => setPasswords({ ...passwords, next: e.target.value })} />
                <TextField type="password" name="admin-new-password-confirmation" autoComplete="new-password" label="Повторите пароль" value={passwords.confirm} onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })} />
              </Box>
              {user.totp_enabled && <TextField label="Код 2FA для подтверждения" value={passwordTotp} onChange={(e) => setPasswordTotp(e.target.value)} />}
              <Button variant="outlined" disabled={busy || !passwords.current || !passwords.next || !passwords.confirm || (user.totp_enabled && !passwordTotp)} onClick={changePassword}>Сменить пароль</Button>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
        <Button color="error" onClick={() => authService.logout()}>Выйти</Button>
        <Button onClick={onClose}>Закрыть</Button>
      </DialogActions>
    </Dialog>
  );
};
